<script>
  import { onDestroy, onMount } from 'svelte';
  import { Download, ExternalLink, LoaderCircle, Maximize2, Play, RefreshCw, VideoOff } from 'lucide-svelte';
  import { normalizeUrl } from '$lib/utils';

  export let src = '';
  export let name = 'video';
  export let mode = 'message';
  export let mime = '';

  let videoEl;
  let activeSourceKey = '';
  let hasMetadata = false;
  let isLoading = false;
  let loadRequested = false;
  let playbackError = false;
  let errorTitle = '';
  let deviceChecked = false;
  let blockedOnDevice = false;
  let mounted = false;

  const MEDIA_MIME_BY_EXT = {
    mp4: 'video/mp4',
    m4v: 'video/mp4',
    webm: 'video/webm',
    ogg: 'video/ogg',
    ogv: 'video/ogg',
    mov: 'video/quicktime',
    m3u8: 'application/vnd.apple.mpegurl'
  };

  const FORMAT_BY_MIME = {
    'video/mp4': 'MP4',
    'video/webm': 'WebM',
    'video/ogg': 'OGV',
    'video/quicktime': 'MOV',
    'application/vnd.apple.mpegurl': 'HLS',
    'application/x-mpegurl': 'HLS'
  };

  function stripQueryHash(value) {
    return String(value || '').split(/[?#]/, 1)[0];
  }

  function getExtension(value) {
    const lastSegment = stripQueryHash(value).split('/').pop() || '';
    return lastSegment.match(/\.([a-z0-9]+)$/i)?.[1]?.toLowerCase() || '';
  }

  function cleanMime(value) {
    return String(value || '').split(';', 1)[0].trim().toLowerCase();
  }

  function isAppleMobileDevice() {
    const ua = navigator.userAgent || '';
    const platform = navigator.platform || '';
    return /iPad|iPhone|iPod/.test(ua) || (platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  }

  function getPlaybackErrorTitle(error) {
    if (error?.code === 2) return 'Không thể tải video';
    if (error?.code === 3) return 'Không thể giải mã video';
    if (error?.code === 4) return 'Định dạng video chưa được hỗ trợ';
    return 'Không thể phát video';
  }

  $: mediaUrl = String(normalizeUrl(src) || '');
  $: mediaExtension = getExtension(mediaUrl);
  $: declaredMime = String(mime || '').trim().toLowerCase();
  $: mediaMime = cleanMime(declaredMime) || MEDIA_MIME_BY_EXT[mediaExtension] || '';
  $: formatLabel = FORMAT_BY_MIME[mediaMime] || mediaExtension.toUpperCase() || 'VIDEO';
  $: hasMediaUrl = Boolean(mediaUrl && mediaUrl !== '/' && !mediaUrl.startsWith('undefined'));
  $: displayName = name || stripQueryHash(mediaUrl).split('/').pop() || 'video';
  $: sourceKey = `${mediaUrl}|${declaredMime}`;

  $: if (sourceKey !== activeSourceKey) {
    activeSourceKey = sourceKey;
    releaseSource();
    playbackError = false;
    errorTitle = '';
    hasMetadata = false;
    isLoading = false;
    loadRequested = false;
    deviceChecked = false;
    blockedOnDevice = false;
    if (mounted) checkDeviceSupport();
  }

  function checkDeviceSupport() {
    if (!hasMediaUrl) {
      blockedOnDevice = false;
      deviceChecked = true;
      return;
    }

    const isAppleMobile = isAppleMobileDevice();
    let unsupportedMime = false;

    if (isAppleMobile && mediaMime) {
      const probe = document.createElement('video');
      unsupportedMime = !probe.canPlayType(declaredMime || mediaMime) && !probe.canPlayType(mediaMime);
    }

    blockedOnDevice = isAppleMobile && unsupportedMime;
    deviceChecked = true;
  }

  function releaseSource() {
    if (!videoEl) return;

    videoEl.pause();
    videoEl.removeAttribute('src');
    if (videoEl.networkState !== HTMLMediaElement.NETWORK_EMPTY) videoEl.load();
  }

  function handleMetadata() {
    hasMetadata = true;
    handleReady();
  }

  function handleReady() {
    playbackError = false;
    errorTitle = '';
    isLoading = false;
  }

  function handleWaiting() {
    if (loadRequested && !playbackError) isLoading = true;
  }

  function handleError() {
    isLoading = false;
    playbackError = true;
    errorTitle = getPlaybackErrorTitle(videoEl?.error);
  }

  async function startPlayback() {
    if (!videoEl || !hasMediaUrl || blockedOnDevice) return;

    loadRequested = true;
    playbackError = false;
    errorTitle = '';
    isLoading = true;
    if (videoEl.getAttribute('src') !== mediaUrl) videoEl.src = mediaUrl;

    try {
      // Keep play() in the tap handler. iOS rejects playback when a load/re-render
      // breaks the user gesture, and repeated load() calls cancel pending play().
      await videoEl.play();
    } catch {
      if (videoEl.error) handleError();
      else isLoading = false;
    }
  }

  function retryPlayback() {
    void startPlayback();
  }

  function handlePlaying() {
    handleReady();
    window.dispatchEvent(new CustomEvent('messenger-video-playing', { detail: videoEl }));
  }

  function pauseWhenAnotherVideoPlays(event) {
    if (event.detail !== videoEl && !videoEl?.paused) videoEl.pause();
  }

  function pauseWhenPageIsHidden() {
    if (document.hidden && !videoEl?.paused) videoEl.pause();
  }

  async function requestFullscreen() {
    if (!videoEl) return;

    try {
      if (videoEl.requestFullscreen) {
        await videoEl.requestFullscreen();
      } else if (videoEl.webkitEnterFullscreen) {
        videoEl.webkitEnterFullscreen();
      } else if (videoEl.webkitRequestFullscreen) {
        videoEl.webkitRequestFullscreen();
      }
    } catch {
      // Native controls still provide fullscreen when the browser blocks this API.
    }
  }

  onMount(() => {
    mounted = true;
    checkDeviceSupport();
    window.addEventListener('messenger-video-playing', pauseWhenAnotherVideoPlays);
    document.addEventListener('visibilitychange', pauseWhenPageIsHidden);
  });

  onDestroy(() => {
    mounted = false;
    window.removeEventListener('messenger-video-playing', pauseWhenAnotherVideoPlays);
    document.removeEventListener('visibilitychange', pauseWhenPageIsHidden);
    releaseSource();
  });
</script>

<section class:modal={mode === 'modal'} class="video-shell" aria-label={`Video: ${displayName}`}>
  {#if mode !== 'modal'}
    <header class="video-topbar">
      <div class="video-title">
        <div class="title-meta">
          <span class="format-pill">{formatLabel}</span>
        </div>
      </div>

      <div class="video-actions">
        {#if hasMediaUrl && loadRequested && !playbackError && !blockedOnDevice}
          <button type="button" title="Toàn màn hình" aria-label="Phát toàn màn hình" on:click={requestFullscreen}>
            <Maximize2 size={17} strokeWidth={2} />
          </button>
        {/if}
        {#if hasMediaUrl}
          <a href={mediaUrl} target="_blank" rel="noopener noreferrer" title="Mở trong tab mới" aria-label="Mở video trong tab mới">
            <ExternalLink size={17} strokeWidth={2} />
          </a>
          <a href={mediaUrl} download={displayName} title="Tải video" aria-label="Tải video">
            <Download size={17} strokeWidth={2} />
          </a>
        {/if}
      </div>
    </header>
  {/if}

  {#if hasMediaUrl && deviceChecked && blockedOnDevice}
    <div class="video-fallback" role="status">
      <span class="fallback-icon"><VideoOff size={27} strokeWidth={1.8} /></span>
      <strong>Cần bản video tương thích iPhone</strong>
      <p>Video này được chặn để tránh Safari bị treo. Hãy dùng bản MP4 H.264/AAC để phát an toàn trên iPhone và iPad.</p>
    </div>
  {:else if hasMediaUrl}
    <div class:initializing={!hasMetadata} class:failed={playbackError} class="video-stage" aria-busy={isLoading}>
      <!-- svelte-ignore a11y_media_has_caption -->
      <video
        class:pending={!loadRequested}
        class:hidden={playbackError}
        bind:this={videoEl}
        controls
        playsinline
        webkit-playsinline="true"
        x-webkit-airplay="allow"
        preload="none"
        aria-label={`Phát ${displayName}`}
        on:loadstart={handleWaiting}
        on:loadedmetadata={handleMetadata}
        on:canplay={handleReady}
        on:playing={handlePlaying}
        on:waiting={handleWaiting}
        on:stalled={handleWaiting}
        on:error={handleError}></video>

      {#if playbackError}
        <div class="video-fallback" role="status">
          <span class="fallback-icon"><VideoOff size={27} strokeWidth={1.8} /></span>
          <strong>{errorTitle}</strong>
          <p>Hãy thử tải lại. Nếu lỗi vẫn còn, bạn có thể mở video ở tab mới hoặc tải xuống thiết bị.</p>
          <button type="button" class="retry-button" on:click={retryPlayback}>
            <RefreshCw size={16} strokeWidth={2} />
            Thử lại
          </button>
        </div>
      {:else if !loadRequested}
        <button
          type="button"
          class="play-prompt"
          disabled={!deviceChecked}
          aria-label={`Phát ${displayName}`}
          on:click={startPlayback}>
          {#if deviceChecked}
            <span class="play-icon"><Play size={25} fill="currentColor" strokeWidth={1.8} /></span>
            <span>Phát video</span>
          {:else}
            <LoaderCircle class="checking-icon" size={24} strokeWidth={1.8} />
            <span>Đang kiểm tra video</span>
          {/if}
        </button>
      {:else if isLoading}
        <div class="loading-indicator" aria-hidden="true">
          <LoaderCircle size={26} strokeWidth={1.8} />
          <span>Đang tải video</span>
        </div>
      {/if}
    </div>
  {:else}
    <div class="video-fallback" role="status">
      <span class="fallback-icon"><VideoOff size={27} strokeWidth={1.8} /></span>
      <strong>Không tìm thấy video</strong>
      <p>Đường dẫn video không hợp lệ hoặc không còn tồn tại.</p>
    </div>
  {/if}
</section>

<style>
  .video-shell {
    position: relative;
    width: min(100%, 40rem);
    overflow: hidden;
    border: 1px solid rgba(148, 163, 184, 0.22);
    border-radius: 1rem;
    color: #f8fafc;
    background: #090d14;
    box-shadow: 0 12px 32px rgba(2, 6, 23, 0.22);
    isolation: isolate;
  }

  .video-shell.modal {
    width: min(calc(100vw - 2rem), 72rem);
    border: 0;
    border-radius: 1.15rem;
    box-shadow: 0 24px 70px rgba(0, 0, 0, 0.42);
  }

  .video-topbar {
    display: flex;
    min-height: 3.05rem;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    padding: 0.5rem 0.75rem 0.5rem 0.9rem;
    border-bottom: 1px solid rgba(148, 163, 184, 0.15);
    background: #111722;
  }

  .video-title {
    min-width: 0;
  }

  .title-meta {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    color: #7dd3fc;
    font-size: 0.58rem;
    font-weight: 750;
    letter-spacing: 0.07em;
    line-height: 1;
    text-transform: uppercase;
  }

  .format-pill {
    padding: 0.18rem 0.38rem;
    border: 1px solid rgba(148, 163, 184, 0.2);
    border-radius: 999px;
    color: #94a3b8;
    background: rgba(148, 163, 184, 0.08);
    font-size: 0.56rem;
  }

  .video-actions {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    gap: 0.35rem;
  }

  .video-actions button,
  .video-actions a {
    display: grid;
    width: 2.25rem;
    height: 2.25rem;
    place-items: center;
    border: 1px solid rgba(148, 163, 184, 0.18);
    border-radius: 0.65rem;
    color: #cbd5e1;
    background: rgba(148, 163, 184, 0.07);
    cursor: pointer;
    transition: border-color 140ms ease, background 140ms ease, color 140ms ease;
  }

  .video-actions button:hover,
  .video-actions a:hover,
  .video-actions button:focus-visible,
  .video-actions a:focus-visible {
    border-color: rgba(125, 211, 252, 0.42);
    color: #ffffff;
    background: rgba(14, 165, 233, 0.15);
    outline: none;
  }

  .video-stage {
    position: relative;
    display: grid;
    min-height: 7rem;
    place-items: center;
    overflow: hidden;
    background: #000;
  }

  .video-stage.initializing {
    aspect-ratio: 16 / 9;
  }

  .video-stage.failed {
    min-height: 15rem;
  }

  video {
    display: block;
    width: 100%;
    max-height: min(68dvh, 34rem);
    object-fit: contain;
    background: #000;
  }

  video.pending { pointer-events: none; }

  video.hidden {
    display: none;
  }

  .video-shell.modal video {
    max-height: calc(100dvh - 6.75rem);
  }

  .play-prompt {
    position: absolute;
    inset: 0;
    display: grid;
    place-items: center;
    align-content: center;
    gap: 0.65rem;
    width: 100%;
    border: 0;
    color: #f8fafc;
    background: radial-gradient(circle at 50% 45%, rgba(14, 165, 233, 0.16), transparent 38%), #05080d;
    font-size: 0.78rem;
    font-weight: 650;
    cursor: pointer;
  }

  .play-prompt:focus-visible {
    box-shadow: inset 0 0 0 2px rgba(125, 211, 252, 0.7);
    outline: none;
  }

  .play-prompt:disabled {
    cursor: wait;
  }

  .play-icon {
    display: grid;
    width: 3.5rem;
    height: 3.5rem;
    place-items: center;
    border: 1px solid rgba(125, 211, 252, 0.35);
    border-radius: 999px;
    color: #e0f2fe;
    background: rgba(14, 165, 233, 0.22);
    box-shadow: 0 10px 28px rgba(2, 132, 199, 0.2);
    transition: transform 140ms ease, background 140ms ease;
  }

  .play-prompt:hover .play-icon,
  .play-prompt:focus-visible .play-icon {
    background: rgba(14, 165, 233, 0.32);
    transform: scale(1.05);
  }

  .play-prompt :global(.checking-icon) {
    animation: spin 0.9s linear infinite;
  }

  .loading-indicator {
    position: absolute;
    inset: 50% auto auto 50%;
    display: flex;
    align-items: center;
    gap: 0.55rem;
    padding: 0.55rem 0.75rem;
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 999px;
    color: #e2e8f0;
    background: rgba(15, 23, 42, 0.78);
    font-size: 0.72rem;
    font-weight: 600;
    pointer-events: none;
    transform: translate(-50%, -50%);
  }

  .loading-indicator :global(svg) {
    animation: spin 0.9s linear infinite;
  }

  .video-fallback {
    display: grid;
    width: 100%;
    min-height: 15rem;
    box-sizing: border-box;
    place-items: center;
    align-content: center;
    gap: 0.55rem;
    padding: 1.5rem;
    color: #94a3b8;
    background: radial-gradient(circle at 50% 0%, rgba(14, 165, 233, 0.1), transparent 55%), #080c12;
    text-align: center;
  }

  .fallback-icon {
    display: grid;
    width: 3.25rem;
    height: 3.25rem;
    margin-bottom: 0.2rem;
    place-items: center;
    border: 1px solid rgba(148, 163, 184, 0.18);
    border-radius: 1rem;
    color: #7dd3fc;
    background: rgba(14, 165, 233, 0.1);
  }

  .video-fallback strong {
    color: #f8fafc;
    font-size: 0.9rem;
  }

  .video-fallback p {
    max-width: 25rem;
    margin: 0;
    font-size: 0.76rem;
    line-height: 1.55;
  }

  .retry-button {
    display: inline-flex;
    align-items: center;
    gap: 0.45rem;
    margin-top: 0.35rem;
    padding: 0.55rem 0.8rem;
    border: 1px solid rgba(125, 211, 252, 0.25);
    border-radius: 0.65rem;
    color: #e0f2fe;
    background: rgba(14, 165, 233, 0.14);
    font-size: 0.76rem;
    font-weight: 650;
    cursor: pointer;
  }

  .retry-button:hover,
  .retry-button:focus-visible {
    border-color: rgba(125, 211, 252, 0.5);
    background: rgba(14, 165, 233, 0.22);
    outline: none;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  @media (max-width: 640px) {
    .video-shell,
    .video-shell.modal {
      width: min(calc(100vw - 1rem), 100%);
      border-radius: 0.9rem;
    }

    .video-topbar {
      min-height: 3.45rem;
      padding: 0.55rem 0.55rem 0.55rem 0.75rem;
    }

    .video-actions {
      gap: 0.25rem;
    }

    .video-actions button,
    .video-actions a {
      width: 2.15rem;
      height: 2.15rem;
      border-radius: 0.6rem;
    }

    video,
    .video-shell.modal video {
      max-height: calc(100dvh - 7rem);
    }

    .video-fallback {
      min-height: 13rem;
      padding: 1.25rem;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .video-actions button,
    .video-actions a,
    .play-icon {
      transition: none;
    }
  }
</style>
